import { Container, IDestroyOptions, Point, Sprite, TextStyle, Text } from "pixi.js";
import { RaidResourcePackage } from "../../resources/RaidResourcePackage";
import { BasePageView } from "../../../../common/components/BasePageView";
import { CommonResourcesPackage } from "../../../../common/resources/CommonResourcesPackage";
import { FadeScreen } from "../../../../game/components/FadeScreen";
import { ICanvasScaler } from "../../../../../../canvas/ICanvasScaler";
import { RewardContainer } from "../../components/RewardContainer";
import { PromiseCompletionSource } from "../../../../../../shared/async/PromiseCompletionSource";
import { TopPanel } from "../../../../game/components/toppanel/TopPanel";
import { IRaidController } from "../../controllers/IRaidController";
import { CoinsCollectAnimationView } from "../../../../common/components/animations/CoinsCollectAnimationView";
import { SoundsResourcePackageWrapper } from "../../../../common/resources/SoundsResourcePackageWrapper";
import { gsap } from "gsap";
import { DiceRollRaidGameResultType } from "../../../../game/flow/impl/diceActions/raidAction/DiceRollRaidGameResultType";
import { GameFont } from "../../../../common/GameFont";
import { RaidTutorialResourcePackage } from "../../resources/RaidTutorialResourcePackage";
import { RaidTargetsData } from "../../../../game/flow/impl/diceActions/raidAction/RaidTargetsData";

export class SummaryPage extends BasePageView {
    private static readonly COINS_BACKGROUND_SCALE = 1.1;
    private static readonly RAID_SUMMARY_LOGO_SCALE = 1;

    private readonly commonResources: CommonResourcesPackage;
    private readonly raidResources: RaidResourcePackage;
    private readonly soundResources: SoundsResourcePackageWrapper;
    private readonly raidController: IRaidController;
    private readonly isFirstShow: boolean;
    private readonly topPanel: TopPanel;
    private readonly tutorialResources: RaidTutorialResourcePackage;
    private readonly raidTargetsData: RaidTargetsData;
    private fadeScreen: FadeScreen;
    private glow: Sprite;
    private background: Sprite;
    private raidSummaryLogo: Sprite;
    private rewardContainer: RewardContainer;
    private showPageTarget: Point;
    private viewCompletionSource: PromiseCompletionSource<void>;
    private coinsCollectAnimation: CoinsCollectAnimationView;
    private coinsCollectPoint: Point;
    private winAmountInCoins: number;
    private currentAnimation: gsap.core.Timeline;

    public constructor(commonResources: CommonResourcesPackage,
        raidResources: RaidResourcePackage,
        soundResources: SoundsResourcePackageWrapper,
        raidController: IRaidController,
        canvasScaler: ICanvasScaler,
        isFirstShow: boolean,
        tutorialResources: RaidTutorialResourcePackage,
        viewCompletionSource: PromiseCompletionSource<void>,
        raidTargetsData: RaidTargetsData,
        topPanel: TopPanel
    ) {
        super(canvasScaler);
        this.commonResources = commonResources;
        this.raidResources = raidResources;
        this.soundResources = soundResources;
        this.raidController = raidController;
        this.isFirstShow = isFirstShow;
        this.tutorialResources = tutorialResources;
        this.viewCompletionSource = viewCompletionSource;
        this.raidTargetsData = raidTargetsData;
        this.winAmountInCoins = raidTargetsData.diceRollRaidTargets.find((item) => item.willBeChosen === true).winAmountInCoins;
        this.topPanel = topPanel;
        this.build();
        this.soundResources.assets_sounds_Collect_Screen_mp3.play();
        this.once("added", this.showComponents.bind(this));
        this.eventMode = "static";
        this.on("pointerup", this.skipAnimations, this);
    }

    protected override build(): void {
        const width = this.canvasScaler.referenceWidth;
        const height = this.canvasScaler.referenceHeight;
        this.fadeScreen = new FadeScreen(0.3, 0, 0, width, height);
        this.fadeScreen.alpha = 1;
        this.addChild(this.fadeScreen);
        this.glow = new Sprite(this.raidResources.assets_views_raid_ui_glow_png);
        this.glow.anchor.set(0.5, 0.5);
        this.addChild(this.glow);
        this.background = new Sprite(this.raidResources.assets_views_raid_ui_raid_samary_bg_png);
        this.background.anchor.set(0.5, 0.5);
        this.addChild(this.background);
        this.raidSummaryLogo = new Sprite(this.raidResources.assets_views_raid_ui_tittle_png);
        this.raidSummaryLogo.anchor.set(0.5, 0.5);
        this.addChild(this.raidSummaryLogo);
        const iconContainer = new Container();
        if (this.raidTargetsData.diceRollRaidGameResult == DiceRollRaidGameResultType.COMPENSATION_WIN) {
            const blockedIcon = new Sprite(this.raidResources.assets_views_raid_ui_shield_png);
            blockedIcon.scale.set(0.2);
            blockedIcon.anchor.set(0.5);
            const blockedIconText = new Text('BLOCKED', new BlockedIconTextStyle);
            blockedIconText.anchor.set(0.5);
            iconContainer.addChild(blockedIcon);
            iconContainer.addChild(blockedIconText);
            blockedIconText.position.set(blockedIcon.position.x, blockedIcon.position.y);
        }
        else {
            const targetIconicon = new Sprite(this.raidResources.assets_views_raid_ui_target_png);
            targetIconicon.scale.set(0.6);
            targetIconicon.anchor.set(0.5);
            iconContainer.addChild(targetIconicon);
        }

        this.rewardContainer = new RewardContainer(this.raidResources, this.tutorialResources, this.commonResources, this.winAmountInCoins, iconContainer, this.isFirstShow);
        this.addChild(this.rewardContainer);
        this.topPanel.bringToFront();
        this.topPanel.visible = false;
        this.coinsCollectPoint = new Point();
        this.coinsCollectAnimation = new CoinsCollectAnimationView(this.commonResources, 15, this.coinsCollectPoint);
        this.addChild(this.coinsCollectAnimation);
        super.build();
    }

    private async showComponents(): Promise<void> {
        const animationTimeLine = this.currentAnimation = new gsap.core.Timeline();
        animationTimeLine.fromTo(this.scale, { x: 2, y: 2 }, { x: 1, y: 1, duration: 0.3, ease: "none" });
        animationTimeLine.fromTo(this, { x: -this.showPageTarget.x, y: -this.showPageTarget.y }, { x: 0, y: 0, duration: 0.3, ease: "none" }, "<");
        if (this.isFirstShow) {
            animationTimeLine.add(this.rewardContainer.getShowHintTween());
        }
        animationTimeLine.fromTo(this.background.scale, { x: 1.2, y: 1.2 }, { x: SummaryPage.COINS_BACKGROUND_SCALE, y: SummaryPage.COINS_BACKGROUND_SCALE, duration: 0.5, ease: "none" }, "<");
        animationTimeLine.add(() => this.startCollecting(), "<70%")
        await animationTimeLine.play();
        animationTimeLine.kill();
    }

    private async hidePage(): Promise<void> {
        this.fadeScreen.fadeOut();
        const hideAnimation = new gsap.core.Timeline();
        hideAnimation.to(this, { alpha: 0, duration: 0.3, ease: "power1.out" });
        await hideAnimation.play();
        hideAnimation.kill();
    }

    private async startCollecting(): Promise<void> {
        if (this.isFirstShow) {
            await this.rewardContainer.getHideHintTween();
        }
        this.topPanel.visible = true;
        this.topPanel.show();
        this.raidController.collectCoins(this.winAmountInCoins);
        this.soundResources.assets_sounds_Coins_Top_Bar_mp3.play();
        await this.coinsCollectAnimation.startAnimation();
        await this.hidePage();
        this.viewCompletionSource.complete();
    }

    private skipAnimations(): void {
        if (this.currentAnimation != null && this.currentAnimation.isActive()) {
            this.currentAnimation.progress(1);
        }
        if (this.coinsCollectAnimation.isActive) {
            this.coinsCollectAnimation.setAnimationProgress(1);
        }
    }

    protected override layout(width: number, height: number): void {
        this.fadeScreen.width = width;
        this.fadeScreen.height = height;
        this.glow.width = this.fadeScreen.width;
        this.glow.height = this.fadeScreen.height;
        this.glow.position.set(width * 0.5, height * 0.5);
        this.background.scale.set(SummaryPage.COINS_BACKGROUND_SCALE, SummaryPage.COINS_BACKGROUND_SCALE);
        this.background.position.set(width * 0.5, height * 0.5);
        this.rewardContainer.position.set(width * 0.5 - 10, height * 0.5 + 30);
        this.raidSummaryLogo.scale.set(SummaryPage.RAID_SUMMARY_LOGO_SCALE, SummaryPage.RAID_SUMMARY_LOGO_SCALE);
        this.raidSummaryLogo.position.set(width * 0.5, height * 0.3 - 20);
        this.showPageTarget = new Point(width * 0.5, height * 0.5);
        this.coinsCollectAnimation.position.set(width * 0.45, height * 0.5);
        this.coinsCollectPoint.set(0, TopPanel.DEFAULT_POSITION_Y - this.coinsCollectAnimation.position.y);
        super.layout(width, height);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.off("pointerup", this.skipAnimations, this);
        this.topPanel.bringToOriginParent();
        super.destroy(options);
    }
}

class BlockedIconTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            fill: "#ffffff",
            align: "center",
            stroke: "#575257",
            strokeThickness: 4,
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 72
        }));
    }
}
